import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navSections } from '@/mocks/portfolio';
import SlotText from '@/components/feature/SlotText';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Scroll to section when arriving on home from another page
  useEffect(() => {
    if (location.pathname !== '/') return;
    if (!location.hash) return;
    setOpen(false);
    const id = location.hash.replace('#', '');
    window.setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 160);
  }, [location.pathname, location.hash]);

  const handleNav = (id: string) => {
    setOpen(false);
    // Already on home — just scroll
    if (location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else if (id === 'top') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }
    // On another page — go home then scroll
    if (id === 'top') {
      navigate('/', { replace: false });
      return;
    }
    navigate(`/#${id}`, { replace: false });
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent'
      }`}
    >
      <div className="mx-auto max-w-[1280px] px-6 md:px-10 h-14 md:h-16 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#top"
          onClick={(e) => { e.preventDefault(); handleNav('top'); }}
          className="flex items-center gap-2.5 cursor-pointer"
        >
          <span className="w-7 h-7 flex items-center justify-center bg-foreground-950 text-background-50 font-heading text-[13px] font-medium">
            KN
          </span>
          <SlotText
            text="Keiji Nakatsu"
            as="span"
            className="hidden sm:block font-label text-[10px] tracking-[0.12em] text-foreground-600 uppercase"
          />
        </a>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-5">
          {navSections.map((item) => (
            <a
              key={item.id}
              href={`#${item.id}`}
              onClick={(e) => { e.preventDefault(); handleNav(item.id); }}
              className="cursor-pointer whitespace-nowrap"
            >
              <SlotText
                text={item.label}
                as="span"
                className="font-label text-[11px] tracking-[0.12em] uppercase text-foreground-700"
              />
            </a>
          ))}
        </nav>

        {/* CTA + hamburger */}
        <div className="flex items-center gap-3">
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNav('contact'); }}
            className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 bg-foreground-950 text-background-50 cursor-pointer whitespace-nowrap"
          >
            <SlotText
              text="Get in touch"
              as="span"
              scrambleColor="oklch(0.98 0.01 100)"
              className="font-label text-[10px] tracking-[0.12em] uppercase"
            />
            <i className="ri-arrow-right-up-line text-sm w-3 h-3 flex items-center justify-center" />
          </a>
          <button
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden w-9 h-9 flex items-center justify-center border border-background-300 cursor-pointer"
          >
            <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-lg w-4 h-4 flex items-center justify-center`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-background-50 border-t border-background-200">
          <nav className="flex flex-col px-6 py-6 gap-4">
            {navSections.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                onClick={(e) => { e.preventDefault(); handleNav(item.id); }}
                className="cursor-pointer"
              >
                <SlotText
                  text={item.label}
                  as="span"
                  className="font-label text-xs tracking-[0.12em] uppercase text-foreground-800"
                />
              </a>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}